// src/components/donors/DonorsTable.js
import React, { useState, useRef, useEffect } from "react";
import { Eye, Pencil, Download, PlusCircle } from "lucide-react";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function DonorsTable({
    donors = [],
    paginationInfo,
    onPageChange,
    onView,
    onEdit,
    onAddDonation, // New prop to handle adding a donation
    currentSearchTerm, // used for export filtering
}) {
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [localSearchTerm, setLocalSearchTerm] = useState("");
    const dropdownRef = useRef(null);

    // --- EXPORT FUNCTIONALITY ---

    const fetchAllDonorsForExport = async () => {
        setIsExporting(true);
        const token = localStorage.getItem("token");
        const API_URL = "https://ims.holdthechild.net";

        try {
            if (!token) {
                throw new Error("Authentication token not found. Please log in.");
            }

            const params = {
                limit: 10000,
                status: "active",
            };
            if (currentSearchTerm) {
                params.search = currentSearchTerm;
            }

            const response = await fetch(
                `${API_URL}/api/donors?` +
                new URLSearchParams(params),
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || "Failed to fetch donors");
            return data.data || [];
        } catch (error) {
            alert("Export error: " + (error.message || "Unknown error"));
            return [];
        } finally {
            setIsExporting(false);
        }
    };

    const mapDonorToExportRow = (donor) => ({
        "Display Name": donor.displayName || donor.name || donor.organization || "",
        "Donor Type": donor.donorType || "",
        "Donor Category": donor.donorCategory || "",
        Organization: donor.organization || "",
        "Contact Person": donor.contactPerson || "",
        "Primary Email": donor.email || "",
        "Alternate Email": donor.alternateEmail || "",
        "Primary Phone": donor.phone?.primary || "",
        "Secondary Phone": donor.phone?.secondary || "",
        Street: donor.address?.street || "",
        City: donor.address?.city || "",
        State: donor.address?.state || "",
        Country: donor.address?.country || "",
        Status: donor.status || "",
        "Total Donations (UGX)": donor.totalDonations || 0,
        "Last Donation Date": donor.lastDonationDate
            ? format(new Date(donor.lastDonationDate), "yyyy-MM-dd")
            : "",
        "Created At": format(new Date(donor.createdAt), "yyyy-MM-dd"),
    });

    const handleExport = async (format) => {
        setExportDropdownOpen(false);
        const allDonors = await fetchAllDonorsForExport();

        if (allDonors.length === 0) {
            alert("No data available to export.");
            return;
        }

        const exportData = allDonors.map(mapDonorToExportRow);

        if (format === "excel") {
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const colWidths = Object.keys(exportData[0]).map((key) => ({
                wch: Math.max(key.length, 20),
            }));
            worksheet["!cols"] = colWidths;
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Donors");
            XLSX.writeFile(workbook, "Donors_Export.xlsx");
        } else if (format === "pdf") {
            const doc = new jsPDF({ orientation: "landscape" });
            autoTable(doc, {
                head: [Object.keys(exportData[0])],
                body: exportData.map((row) => Object.values(row)),
                headStyles: { fillColor: [237, 125, 49] },
                didDrawPage: (data) => {
                    doc.text("Donor Report", 20, 15);
                },
            });
            doc.save("Donors_Report.pdf");
        } else if (format === "csv") {
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
            const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.setAttribute("download", "Donors_Export.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Close export dropdown if clicked outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setExportDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Filter donors locally based on the search term
    const filteredDonors = donors.filter((donor) => {
        const search = localSearchTerm.toLowerCase();
        return (
            (donor.displayName || donor.name || donor.organization || "")
                .toLowerCase()
                .includes(search) ||
            (donor.donorCategory || "").toLowerCase().includes(search) ||
            (donor.status || "").toLowerCase().includes(search)
        );
    });

    // Pagination render function
    const renderPagination = () => {
        if (!paginationInfo || paginationInfo.totalPages <= 1) return null;
        const { currentPage: current, totalPages } = paginationInfo;
        const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

        return (
            <div className="flex justify-center mt-4 space-x-2">
                <button
                    onClick={() => onPageChange(current - 1)}
                    disabled={current === 1}
                    className="px-3 py-1 rounded border bg-gray-100 text-gray-600 disabled:opacity-50"
                >
                    Previous
                </button>
                {pageNumbers.map((page) => (
                    <button
                        key={page}
                        onClick={() => onPageChange(page)}
                        className={`px-3 py-1 rounded border ${current === page
                            ? "bg-orange-500 text-white"
                            : "bg-gray-100 text-gray-600"
                            }`}
                    >
                        {page}
                    </button>
                ))}
                <button
                    onClick={() => onPageChange(current + 1)}
                    disabled={current === totalPages}
                    className="px-3 py-1 rounded border bg-gray-100 text-gray-600 disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        );
    };

    return (
        <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Donor Profiles</h2>

            <div className="mb-4 flex justify-between items-center">
                <input
                    type="text"
                    placeholder="Search donors..."
                    value={localSearchTerm}
                    onChange={(e) => setLocalSearchTerm(e.target.value)}
                    className="border rounded px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />

                <div className="relative" ref={dropdownRef}>
                    <button
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                        disabled={isExporting}
                        className="inline-flex items-center gap-2 justify-center rounded bg-orange-500 px-4 py-2 text-white hover:bg-orange-600 focus:outline-none disabled:bg-orange-300"
                    >
                        {isExporting ? (
                            "Exporting..."
                        ) : (
                            <>
                                <Download size={16} /> Export
                            </>
                        )}
                    </button>
                    {exportDropdownOpen && (
                        <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                            <div className="py-1">
                                {["Excel", "PDF", "CSV"].map((format) => (
                                    <button
                                        key={format}
                                        onClick={() => handleExport(format.toLowerCase())}
                                        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100"
                                    >
                                        Export as {format}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead className="bg-orange-100">
                        <tr>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">
                                Display Name
                            </th>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">
                                Category
                            </th>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">
                                Total Donations
                            </th>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">
                                Last Donation
                            </th>
                            <th className="p-3 text-left text-sm font-semibold text-orange-600">
                                Status
                            </th>
                            <th className="p-3 text-center text-sm font-semibold text-orange-600">
                                Add Donation
                            </th>
                            <th className="p-3 text-center text-sm font-semibold text-orange-600">
                                Actions
                            </th>

                        </tr>
                    </thead>
                    <tbody>
                        {filteredDonors.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="p-4 text-center text-gray-500">
                                    No donors found.
                                </td>
                            </tr>
                        ) : (
                            filteredDonors.map((donor) => (
                                <tr
                                    key={donor._id}
                                    className="border-b border-gray-200 hover:bg-gray-50"
                                >
                                    <td className="p-3 whitespace-nowrap font-medium">
                                        {donor.displayName || donor.name || donor.organization || "N/A"}
                                    </td>
                                    <td className="p-3 whitespace-nowrap capitalize">
                                        {donor.donorCategory}
                                    </td>
                                    <td className="p-3 whitespace-nowrap">
                                        {new Intl.NumberFormat("en-US", {
                                            style: "currency",
                                            currency: "USD",
                                        }).format(donor.totalDonations || 0)}
                                    </td>
                                    <td className="p-3 whitespace-nowrap">
                                        {donor.lastDonationDate
                                            ? format(new Date(donor.lastDonationDate), "dd MMM yyyy")
                                            : "N/A"}
                                    </td>
                                    <td className="p-3 whitespace-nowrap">
                                        <span
                                            className={`px-2 py-1 text-xs font-semibold rounded-full ${donor.status === "active"
                                                ? "bg-green-100 text-green-800"
                                                : "bg-yellow-100 text-yellow-800"
                                                }`}
                                        >
                                            {donor.status?.toUpperCase() || "UNKNOWN"}
                                        </span>
                                    </td>
                                    <td className="p-3 whitespace-nowrap text-center">
                                        <button
                                            onClick={() => onAddDonation(donor._id)}
                                            className="p-2 text-green-600 hover:text-green-800"
                                            title="Add Donation"
                                        >
                                            <PlusCircle size={18} />
                                        </button>
                                    </td>
                                    <td className="p-3 whitespace-nowrap text-center">
                                        <button
                                            onClick={() => onView(donor._id)}
                                            className="p-2 text-blue-600 hover:text-blue-800"
                                            title="View Details"
                                        >
                                            <Eye size={18} />
                                        </button>
                                        <button
                                            onClick={() => onEdit(donor._id)}
                                            className="p-2 text-orange-600 hover:text-orange-800"
                                            title="Edit Donor"
                                        >
                                            <Pencil size={18} />
                                        </button>
                                    </td>

                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {renderPagination()}
        </div>
    );
}